import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { getTracks, getBrowserData } from '../api';
import usePlayerStore from '../store/playerStore';
import useAuthStore from '../store/authStore';
import NavControls from '../components/NavControls';
import TrackTable from '../components/TrackTable';
import useDebounce from '../hooks/useDebounce';


// Map frontend sort keys to Django ordering field names
const SORT_KEY_MAP = {
  artist_name: 'artist__name',
  album_title: 'album__title',
  genre_name: 'genre__name',
};

function StarRating({ rating, onRate }) {
  const stars = Math.round(rating / 20);
  return (
    <div className="stars">
      {[1,2,3,4,5].map(i => (
        <span key={i} className={`star ${i <= stars ? 'filled' : ''}`}
          onClick={(e) => { e.stopPropagation(); onRate(i * 20); }}>★</span>
      ))}
    </div>
  );
}

function BrowserPanel({ title, items, selected, onSelect }) {
  const allLabel = `All (${items.length} ${title}s)`;
  const listRef = useRef(null);
  const [typeahead, setTypeahead] = useState('');
  const typeaheadTimer = useRef(null);

  // Full list with '' representing "All" at index 0
  const allItems = ['', ...items];
  const selectedIndex = allItems.indexOf(selected || '');

  const scrollToIndex = (index) => {
    if (!listRef.current) return;
    const rows = listRef.current.querySelectorAll('.browser-panel-item');
    if (rows[index]) rows[index].scrollIntoView({ block: 'nearest' });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(selectedIndex + 1, allItems.length - 1);
      onSelect(allItems[next]);
      setTimeout(() => scrollToIndex(next), 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = Math.max(selectedIndex - 1, 0);
      onSelect(allItems[prev]);
      setTimeout(() => scrollToIndex(prev), 0);
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // Typeahead — accumulate characters, reset after 800ms pause
      const next = typeahead + e.key.toLowerCase();
      setTypeahead(next);
      if (typeaheadTimer.current) clearTimeout(typeaheadTimer.current);
      typeaheadTimer.current = setTimeout(() => setTypeahead(''), 800);

      const match = items.find(item => item.toLowerCase().startsWith(next));
      if (match) {
        onSelect(match);
        const matchIndex = allItems.indexOf(match);
        setTimeout(() => scrollToIndex(matchIndex), 0);
      }
    }
  };

  return (
    <div className="browser-panel" tabIndex={0} onKeyDown={handleKeyDown}>
      <div className="browser-panel-header">{title}</div>
      <div className="browser-panel-list" ref={listRef}>
        <div
          className={`browser-panel-item ${!selected ? 'selected' : ''}`}
          onClick={() => onSelect('')}
        >
          {allLabel}
        </div>
        {items.map(item => (
          <div
            key={item}
            className={`browser-panel-item ${selected === item ? 'selected' : ''}`}
            onClick={() => onSelect(item)}
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Library() {
  const { user } = useAuthStore();
  const isAdmin = user?.is_staff === true;
  const { queue, currentTrack } = usePlayerStore();
  const [search, setSearch] = useState('');
  // Multi-column sort history — first entry is primary, rest are secondaries
  const [ordering, setOrdering] = useState(['artist__name', 'album__title', 'track_number']);
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedArtist, setSelectedArtist] = useState('');
  const [selectedAlbum, setSelectedAlbum] = useState('');
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => {
      setWindowHeight(window.innerHeight);
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // On mobile with an active queue and no search term, show the queue directly.
  // Use matchMedia directly (not isMobile state) so fullscreen viewport transitions
  // can't briefly flip this to false and trigger an unnecessary API fetch.
  const showingQueue = window.matchMedia('(max-width: 768px)').matches && !search && queue.length > 0 && !!currentTrack;

  // Debounce filter values so rapid clicks don't fire multiple requests
  const debouncedSearch = useDebounce(search, 300);
  const debouncedGenre = useDebounce(selectedGenre, 150);
  const debouncedArtist = useDebounce(selectedArtist, 150);
  const debouncedAlbum = useDebounce(selectedAlbum, 150);

  // Lightweight browser panel data — not needed on mobile
  const { data: browserData } = useQuery(
    ['browser', debouncedGenre, debouncedArtist],
    () => getBrowserData({
      genre: debouncedGenre || undefined,
      artist: debouncedArtist || undefined,
    }).then(r => r.data),
    { keepPreviousData: true, staleTime: 5 * 60 * 1000, enabled: !isMobile }
  );

  const genres = browserData?.genres || [];
  const artists = browserData?.artists || [];
  const albums = browserData?.albums || [];

  const orderingStr = ordering.join(',');
  const queryKey = ['tracks', debouncedSearch, orderingStr, debouncedGenre, debouncedArtist, debouncedAlbum];

  const {
    data: tracksData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
  } = useInfiniteQuery(
    queryKey,
    ({ pageParam = 1 }) => {
      const params = { ordering: orderingStr, page_size: 100, page: pageParam };
      if (debouncedSearch) {
        // A bare 4-digit number is treated as a year filter
        if (/^\d{4}$/.test(debouncedSearch.trim())) {
          params.year = debouncedSearch.trim();
        } else {
          params.search = debouncedSearch;
        }
      }
      if (debouncedGenre) params.genre__name = debouncedGenre;
      if (debouncedArtist) params.artist__name = debouncedArtist;
      if (debouncedAlbum) params.album__title = debouncedAlbum;
      return getTracks(params).then(r => r.data);
    },
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next, window.location.origin);
          return parseInt(url.searchParams.get('page'), 10);
        }
        return undefined;
      },
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      enabled: !showingQueue,
    }
  );

  const apiTracks = useMemo(
    () => tracksData?.pages?.flatMap(p => p.results) ?? [],
    [tracksData]
  );
  const tracks = showingQueue ? queue : apiTracks;
  const totalCount = showingQueue ? queue.length : (tracksData?.pages?.[0]?.count ?? 0);

  useEffect(() => {
    if (isLoading) document.body.classList.add('loading');
    else document.body.classList.remove('loading');
    return () => document.body.classList.remove('loading');
  }, [isLoading]);

  const handleGenreSelect = (val) => {
    setSelectedGenre(val);
    setSelectedArtist('');
    setSelectedAlbum('');
  };

  const handleArtistSelect = (val) => {
    setSelectedArtist(val);
    setSelectedAlbum('');
  };

  const handleServerSort = useCallback((field) => {
    const serverField = SORT_KEY_MAP[field] || field;
    setOrdering(prev => {
      const primaryField = prev[0]?.replace(/^-/, '');
      if (primaryField === serverField) {
        // Toggle direction on primary sort
        const toggled = prev[0].startsWith('-') ? serverField : `-${serverField}`;
        return [toggled, ...prev.slice(1)];
      }
      // Move field to front; secondaries retain their direction
      const without = prev.filter(f => f.replace(/^-/, '') !== serverField);
      return [serverField, ...without];
    });
  }, []);

  // Height available for the virtual track list
  // Mobile has no browser panels, so more space is available
  const listHeight = Math.max(200, windowHeight - (isMobile ? 300 : 490));

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1>Songs</h1>
          <NavControls />
        </div>
        <p>
          {isLoading
            ? 'Loading…'
            : `${tracks.length}${totalCount > tracks.length ? ` of ${totalCount}` : ''} tracks`}
          {isFetching && !isLoading && (
            <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>· Loading more…</span>
          )}
        </p>
      </div>

      <div className="search-bar">
        <input
          className="search-input"
          placeholder="Search songs, artists, albums… or type a year (e.g. 1971)"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {!isMobile && (
        <div className="browser-bar">
          <BrowserPanel
            title="Genre"
            items={genres}
            selected={selectedGenre}
            onSelect={handleGenreSelect}
          />
          <BrowserPanel
            title="Artist"
            items={artists}
            selected={selectedArtist}
            onSelect={handleArtistSelect}
          />
          <BrowserPanel
            title="Album"
            items={albums}
            selected={selectedAlbum}
            onSelect={val => setSelectedAlbum(val)}
          />
        </div>
      )}

      {showingQueue && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, padding: '0 2px' }}>
          ▶ Current queue — search to filter songs
        </div>
      )}

      {isLoading && !showingQueue ? (
        <p style={{ color: 'var(--text-muted)', padding: '20px' }}>Loading…</p>
      ) : (
        <TrackTable
          tracks={tracks}
          queryKey={queryKey}
          editable={isAdmin}
          deletable={isAdmin ? 'library' : null}
          onSort={handleServerSort}
          serverOrdering={ordering}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingMore={isFetching && !isLoading}
          listHeight={listHeight}
        />
      )}
    </div>
  );
}
